import { useCallback, useEffect, useRef } from 'react';
import type { MailEditorRef } from '@/components/mail-editor';
import { useMailEditorStore } from '@/stores/mailEditorStore';
import type { AttachmentsModel } from '@/stores/mailEditorStore';

interface UseEditorContentSyncOptions {
  /** 编辑器引用 */
  editorRef: React.RefObject<MailEditorRef>;
  /** 启用同步的模式，只有在此模式下才同步 */
  enabledMode: 'window' | 'dialog';
  /** 可选的额外设置函数，比如设置 dialogSubject */
  onApply?: (options: { content: string; attachments: AttachmentsModel[]; subject: string }) => void;
  /** 弹窗打开状态，用于重置状态（可选，如果不传则使用 store 中的 dialogVisible） */
  open?: boolean;
}

/**
 * 统一的编辑器内容同步 Hook
 * 处理编辑器创建和数据变更时的内容设置逻辑
 *
 * 逻辑说明：
 * 1. 当编辑器模式切换为 enabledMode 且弹窗打开时，执行初始化设置
 * 2. 当编辑器创建时，如果有待设置的数据则立即设置
 * 3. 使用 ref 控制时机，确保初始化完成后不再执行内容检查和变更调用操作
 *
 * 注意：content、attachments、subject、editorMode 等数据直接从 useMailEditorStore 中获取
 */
export function useEditorContentSync({ editorRef, enabledMode, onApply, open }: UseEditorContentSyncOptions) {
  // 从 store 中获取数据
  const content = useMailEditorStore(state => state.content);
  const contentUpdateCount = useMailEditorStore(state => state.contentUpdateCount);
  const attachments = useMailEditorStore(state => state.attachments);
  const subject = useMailEditorStore(state => state.subject);
  const editorMode = useMailEditorStore(state => state.editorMode);
  const dialogVisible = useMailEditorStore(state => state.dialogVisible);

  // 使用传入的 open 或 store 中的 dialogVisible
  const isOpen = open !== undefined ? open : dialogVisible;

  // 标记编辑器是否已创建
  const editorCreatedRef = useRef(false);
  // 标记是否有待设置的数据
  const pendingDataRef = useRef(false);
  // 标记是否已经完成初始化，初始化完成后不再执行内容检查和变更调用操作
  const initializedRef = useRef(false);
  // 使用 ref 存储最新的值，避免在依赖中直接使用导致循环
  const contentRef = useRef(content);
  const attachmentsRef = useRef(attachments);
  const subjectRef = useRef(subject);
  const onApplyRef = useRef(onApply);
  // 记录上一次的 editorMode 和 isOpen，用于检测模式切换和弹窗打开
  const prevEditorModeRef = useRef(editorMode);
  const prevIsOpenRef = useRef(isOpen);

  // 更新 ref 中的最新值
  useEffect(() => {
    contentRef.current = content;
    attachmentsRef.current = attachments;
    subjectRef.current = subject;
    onApplyRef.current = onApply;
  }, [content, attachments, subject, onApply]);

  /**
   * 统一的设置内容和附件到编辑器的函数
   * 如果编辑器已创建，立即设置；如果未创建，标记为待设置
   */
  const applyContentToEditor = useCallback(() => {
    // 如果已经初始化完成，不再执行
    if (initializedRef.current) {
      return;
    }

    if (editorRef.current && editorCreatedRef.current) {
      // 编辑器已创建，立即设置
      const currentContent = contentRef.current;
      const currentAttachments = attachmentsRef.current;
      const currentSubject = subjectRef.current;

      editorRef.current.setContent(currentContent);
      editorRef.current.setAttachments(currentAttachments);
      // 调用额外的设置函数
      onApplyRef.current?.({ content: currentContent, attachments: currentAttachments, subject: currentSubject });
      pendingDataRef.current = false;
      // 标记初始化完成
      initializedRef.current = true;
    } else {
      // 编辑器未创建，标记为待设置
      pendingDataRef.current = true;
    }
  }, [editorRef]);

  /**
   * 编辑器创建后，检查是否有待设置的数据并应用
   */
  const onEditCreated = useCallback(() => {
    // 不能直接拿到实例，因为编辑器是懒加载的，所以需要使用setTimeout
    setTimeout(() => {
      editorCreatedRef.current = true;
      // 如果有待设置的数据且未初始化完成，立即应用
      if (pendingDataRef.current && !initializedRef.current) {
        applyContentToEditor();
      }
    }, 200);
  }, [applyContentToEditor]);

  // 监听内容更新次数，如果内容更新次数发生变化，则设置内容到编辑器
  useEffect(() => {
    if (editorRef.current && editorMode === prevEditorModeRef.current) {
      editorRef.current.setContent(content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentUpdateCount]);

  /**
   * 监听编辑器模式变更和弹窗打开状态
   * 只在模式切换为 enabledMode 且弹窗打开时执行初始化
   * 初始化完成后不再执行
   */
  useEffect(() => {
    const modeChanged = prevEditorModeRef.current !== editorMode;
    const openChanged = prevIsOpenRef.current !== isOpen;
    const shouldInitialize = editorMode === enabledMode && isOpen;

    // 更新上一次的值
    prevEditorModeRef.current = editorMode;
    prevIsOpenRef.current = isOpen;

    // 如果弹窗关闭，重置初始化状态
    if (isOpen === false) {
      initializedRef.current = false;
      editorCreatedRef.current = false;
      pendingDataRef.current = false;
      return;
    }

    // 只在模式切换为 enabledMode 且弹窗打开时执行初始化
    // 且之前未初始化完成
    if (shouldInitialize && (modeChanged || openChanged) && !initializedRef.current) {
      // 延迟执行以确保编辑器已初始化
      setTimeout(() => {
        applyContentToEditor();
      }, 100);
    }
  }, [editorMode, enabledMode, isOpen, applyContentToEditor]);

  return {
    onEditCreated,
  };
}
